import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { WhatsAppInboxMessage } from '@zrsystem/contracts';

import {
  deleteWhatsAppInboxMessageForEveryone,
  editWhatsAppInboxMessage,
} from '@/features/notification/api/whatsapp-inbox.api.js';
import {
  markWhatsAppInboxMessageEdited,
  markWhatsAppInboxMessageRevoked,
} from '@/features/notification/lib/whatsapp-inbox-message-overlay.js';
import { notificationQueryKeys } from '@/features/notification/query-keys.js';

function patchMessage(
  queryClient: ReturnType<typeof useQueryClient>,
  placeId: string,
  chatId: string,
  messageId: string,
  map: (message: WhatsAppInboxMessage) => WhatsAppInboxMessage,
): void {
  queryClient.setQueryData<WhatsAppInboxMessage[]>(
    notificationQueryKeys.whatsappInboxMessages(placeId, chatId),
    (current) => current?.map((message) => (message.id === messageId ? map(message) : message)),
  );
}

export function useEditWhatsAppInboxMessage(placeId: string, chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: string; text: string }) =>
      editWhatsAppInboxMessage(placeId, chatId, input.messageId, input.text),
    onSuccess: (_result, input) => {
      markWhatsAppInboxMessageEdited(placeId, input.messageId, input.text);
      patchMessage(queryClient, placeId, chatId, input.messageId, (message) => ({
        ...message,
        body: input.text,
      }));
    },
  });
}

export function useDeleteWhatsAppInboxMessage(placeId: string, chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      deleteWhatsAppInboxMessageForEveryone(placeId, chatId, messageId),
    onSuccess: (_result, messageId) => {
      markWhatsAppInboxMessageRevoked(placeId, messageId);
      patchMessage(queryClient, placeId, chatId, messageId, (message) => ({
        ...message,
        body: null,
        hasMedia: false,
        media: null,
        mediaUrl: null,
      }));
    },
  });
}
